import { useMemo } from "react";
import { Platform, Text, View } from "react-native";
import Svg, { Circle, G, Line, Polygon, Rect, Text as SvgText } from "react-native-svg";

import type { FloorPlanDocument, FloorPlanPoint } from "@/lib/shows/floorPlan";
import { layoutShowMapPins } from "@/lib/shows/showWayfinding";
import { useThemeColors } from "@/theme/theme";

export type ShowHallGroup = {
  participationId: string;
  tableIds: string[];
};

const SIZE = { width: 390, height: 200 };
const PAD = 10;
const ENTRANCE = /(?:^|\s)(?:step-free\s+)?entrance(?:\s|$)/i;

const points = (polygon: readonly FloorPlanPoint[]) =>
  polygon.map((point) => `${point.x},${point.y}`).join(" ");

const average = (polygon: readonly FloorPlanPoint[]) => ({
  x: polygon.reduce((sum, point) => sum + point.x, 0) / polygon.length,
  y: polygon.reduce((sum, point) => sum + point.y, 0) / polygon.length,
});

/** A focus-free hall strip: the numbered Vendor rows below it remain the only tab stops. */
export function ShowHallStrip({ document, groups }: {
  document: FloorPlanDocument;
  groups: ShowHallGroup[];
}) {
  const c = useThemeColors();
  const viewport = useMemo(() => {
    const xs = document.boundary.map((point) => point.x);
    const ys = document.boundary.map((point) => point.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const width = Math.max(1, Math.max(...xs) - minX);
    const height = Math.max(1, Math.max(...ys) - minY);
    const scale = Math.min((SIZE.width - PAD * 2) / width, (SIZE.height - PAD * 2) / height);
    return {
      minX,
      minY,
      scale,
      tx: (SIZE.width - width * scale) / 2,
      ty: (SIZE.height - height * scale) / 2,
    };
  }, [document]);
  const lit = useMemo(
    () => new Set(groups.flatMap((group) => group.tableIds)),
    [groups],
  );
  const pins = useMemo(
    () => layoutShowMapPins(document, groups, 1, SIZE),
    [document, groups],
  );
  const entrance = document.areas.find((area) => !area.obstructs && ENTRANCE.test(area.name));
  const entrancePoint = entrance?.polygon.length ? average(entrance.polygon) : null;
  const toX = (x: number) => viewport.tx + (x - viewport.minX) * viewport.scale;
  const toY = (y: number) => viewport.ty + (y - viewport.minY) * viewport.scale;

  return (
    <View
      testID="show-location"
      className="overflow-hidden rounded-xl border border-border bg-surface"
      accessibilityRole="image"
      accessibilityLabel={`${groups.length} Vendor locations in the Show hall`}
      style={{ height: SIZE.height, width: "100%", maxWidth: SIZE.width }}
    >
      <Svg
        width="100%"
        height={SIZE.height}
        viewBox={`0 0 ${SIZE.width} ${SIZE.height}`}
        accessible={Platform.OS === "web" ? undefined : false}
        focusable={false}
        pointerEvents="none"
      >
        <G
          transform={`translate(${viewport.tx} ${viewport.ty}) scale(${viewport.scale}) translate(${-viewport
            .minX} ${-viewport.minY})`}
        >
          <Polygon
            points={points(document.boundary)}
            fill={c.surface}
            stroke={c.mutedForeground}
            strokeWidth={2 / viewport.scale}
          />
          {document.areas.filter((area) => area.obstructs).map((area) => (
            <Polygon
              key={area.id}
              points={points(area.polygon)}
              fill={c.mutedForeground}
              fillOpacity={0.18}
              stroke={c.mutedForeground}
              strokeWidth={1 / viewport.scale}
            />
          ))}
          {document.tables.map((table) => {
            const active = lit.has(table.id);
            return (
              <Rect
                key={table.id}
                x={table.center.x - table.width / 2}
                y={table.center.y - table.depth / 2}
                width={table.width}
                height={table.depth}
                fill={active ? c.primary : "transparent"}
                fillOpacity={active ? 0.28 : 0}
                stroke={active ? c.primary : c.border}
                strokeWidth={(active ? 3 : 1) / viewport.scale}
                transform={`rotate(${table.rotation} ${table.center.x} ${table.center.y})`}
              />
            );
          })}
        </G>
        {pins.filter((pin) => pin.hasLeader).map((pin) => (
          <Line
            key={`leader-${pin.groupIndex}`}
            x1={pin.anchorX}
            y1={pin.anchorY}
            x2={pin.x}
            y2={pin.y}
            stroke={c.primary}
            strokeOpacity={0.6}
            strokeWidth={1}
          />
        ))}
        {pins.map((pin) => (
          <G key={`pin-${pin.groupIndex}`}>
            <Circle
              cx={pin.x}
              cy={pin.y}
              r={14}
              fill={c.surface}
              stroke={c.primary}
              strokeWidth={2}
            />
            <SvgText
              x={pin.x}
              y={pin.y + 4}
              fill={c.primary}
              fontSize={12}
              fontWeight="700"
              textAnchor="middle"
            >
              {pin.groupIndex + 1}
            </SvgText>
          </G>
        ))}
        {entrancePoint
          ? (
            <SvgText
              x={Math.max(44, Math.min(SIZE.width - 44, toX(entrancePoint.x)))}
              y={Math.max(12, Math.min(SIZE.height - 4, toY(entrancePoint.y)))}
              fill={c.mutedForeground}
              fontSize={10}
              fontWeight="700"
              textAnchor="middle"
            >
              ENTRANCE
            </SvgText>
          )
          : null}
      </Svg>
      <Text className="sr-only">Use the numbered Vendor list for table details.</Text>
    </View>
  );
}
